'use client';

import { ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { KajianEventPreview } from './landing-navbar-kajian-event-preview';
import {
  KAJIAN_EVENT_LINK,
  KajianCategoryGrid,
  KajianCategoryTitle,
  KajianEventTitle,
  KajianUtilityCards,
} from './landing-navbar-kajian-links';
import { SOLUTIONS } from '@/app/solusi/data/solutions';
import { useBlogCategories } from '@/hooks/use-blog-categories';
import { useFeaturedEvent } from '@/hooks/use-featured-event';
import { useRegulationHubs } from '@/hooks/use-regulation-hubs';

export const PRODUCTS_LIST = [
  {
    name: 'Tabel Ekstraksi',
    href: '/product/tabular-review',
  },
  {
    name: 'Tinjau Dokumen',
    href: '/product/tinjau-dokumen',
  },
  {
    name: 'Riset Cepat (Jivara)',
    href: '/product/riset-cepat',
  },
  {
    name: 'Konsolidasi Aturan',
    href: '/product/konsolidasi-aturan',
  },
];

export const NAV_LINKS = [
  { href: '/tutorial', label: 'Panduan', key: 'panduan' as const },
  { href: '/pricing', label: 'Harga', key: 'harga' as const },
  { href: '/blog', label: 'Kajian', key: 'kajian' as const },
  { href: '/berita', label: 'Berita', key: 'berita' as const },
  { href: '/about', label: 'Tentang Kami', key: 'tentang-kami' as const },
];

interface DropdownProps {
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  textColor: string;
  textColorActive: string;
  activeLink?: string;
}

export function ProductDropdown({ isOpen, onToggle, textColor, textColorActive, activeLink }: DropdownProps) {
  return (
    <div
      className="relative"
      onMouseEnter={() => onToggle(true)}
      onMouseLeave={() => onToggle(false)}
    >
      <button
        className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${
          activeLink === 'product' ? textColorActive : textColor
        }`}
      >
        Produk
        <ChevronDown
          size={16}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 pt-2 z-50">
          <div className="min-w-[200px] bg-white border border-neutral-200 rounded-lg shadow-lg p-1.5 animate-fade-in text-neutral-900">
            {PRODUCTS_LIST.map((product) => {
              return (
                <Link
                  key={product.href}
                  href={product.href}
                  className="block px-3 py-1.5 rounded-lg hover:bg-neutral-200 transition-colors text-left"
                >
                  <div className="text-xs font-medium text-neutral-900">{product.name}</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function SolusiDropdown({ isOpen, onToggle, textColor, textColorActive, activeLink }: DropdownProps) {
  return (
    <div
      className="relative"
      onMouseEnter={() => onToggle(true)}
      onMouseLeave={() => onToggle(false)}
    >
      <Link
        href="/solusi"
        className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${
          activeLink === 'solusi' ? textColorActive : textColor
        }`}
      >
        Solusi
        <ChevronDown
          size={16}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </Link>

      {isOpen && (
        <div className="absolute top-full left-0 pt-2 z-50">
          <div className="min-w-[240px] bg-white border border-neutral-200 rounded-lg shadow-lg p-1.5 animate-fade-in text-neutral-900">
            {SOLUTIONS.map((solution) => {
              return (
                <Link
                  key={solution.slug}
                  href={solution.href}
                  className="block px-3 py-1.5 rounded-lg hover:bg-neutral-200 transition-colors text-left"
                >
                  <div className="text-xs font-medium text-neutral-900">{solution.name}</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The "Event" menu item with the separator below it. It sits above the
 * category grid when no promoted event exists, and moves to the right
 * column, directly above the promoted event card, when one does. The
 * promoted event is the admin-highlighted one, falling back to the nearest
 * upcoming event when nothing is highlighted.
 */
export function KajianDropdown({ isOpen, onToggle, textColor, textColorActive, activeLink }: DropdownProps) {
  const { data: categoriesData } = useBlogCategories();
  // Deferred until the dropdown opens so the events and hubs APIs are not hit on load.
  const { data: eventsData } = useFeaturedEvent(isOpen);
  const { data: hubsData } = useRegulationHubs(isOpen);
  const categories = categoriesData?.data ?? [];
  const featuredEvent = eventsData?.data?.[0] ?? null;
  const hub = hubsData?.data?.[0] ?? null;
  const hubHref = hub ? `/blog/hub/${hub.slug}` : null;

  return (
    <div
      className="relative"
      onMouseEnter={() => onToggle(true)}
      onMouseLeave={() => onToggle(false)}
    >
      <Link
        href="/blog"
        className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${
          activeLink === 'kajian' ? textColorActive : textColor
        }`}
      >
        Kajian
        <ChevronDown
          size={16}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </Link>

      {isOpen && (
        <div className="absolute top-full left-0 pt-2 z-50">
          <div
            className={`bg-white border border-neutral-200 rounded-lg shadow-lg p-1.5 animate-fade-in text-neutral-900 ${
              featuredEvent ? 'flex min-w-[660px]' : 'min-w-[360px]'
            }`}
          >
            <div className="flex min-w-[340px] flex-col">
              <KajianCategoryTitle />
              <KajianCategoryGrid categories={categories} />
              <KajianUtilityCards hubHref={hubHref} />
            </div>

            {featuredEvent && (
              <>
                <div className="border-l border-neutral-200 my-1 mx-1" />
                <div className="w-[300px] shrink-0">
                  <KajianEventTitle />
                  <KajianEventPreview
                    event={featuredEvent}
                    isHighlighted={featuredEvent.isHighlighted}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isTransparent: boolean;
}

export function MobileMenu({ isOpen, onClose, isTransparent }: MobileMenuProps) {
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [isSolusiOpen, setIsSolusiOpen] = useState(false);
  const [isKajianOpen, setIsKajianOpen] = useState(false);
  const { data: categoriesData } = useBlogCategories();
  const categories = categoriesData?.data ?? [];

  if (!isOpen) return null;

  const mobileMenuBg = isTransparent ? 'bg-neutral-900' : 'bg-white';
  const mobileMenuText = isTransparent ? 'text-white' : 'text-neutral-900';
  const mobileMenuHover = isTransparent ? 'hover:bg-white/5' : 'hover:bg-neutral-50';

  const linkClassName = `text-base font-medium ${mobileMenuText} ${isTransparent ? 'hover:text-white/80' : 'hover:text-neutral-600'} transition-colors px-4 py-3 rounded-lg ${mobileMenuHover}`;

  return (
    <div className={`md:hidden fixed inset-0 z-40 ${mobileMenuBg} pt-24 pb-8 px-6 overflow-y-auto ${mobileMenuText} animate-fade-in`}>
      <div className="flex flex-col gap-4 max-w-sm mx-auto">
        <nav className="flex flex-col gap-1">
          <button
            onClick={() => setIsProductOpen(!isProductOpen)}
            className={`flex items-center justify-between w-full text-lg font-medium ${mobileMenuText} ${isTransparent ? 'hover:text-white/80' : 'hover:text-neutral-600'} transition-colors px-4 py-3 rounded-lg ${mobileMenuHover}`}
          >
            Produk
            <ChevronDown
              size={18}
              className={`transition-transform ${isProductOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {isProductOpen && (
            <div className="flex flex-col gap-1">
              {PRODUCTS_LIST.map((product) => (
                <Link key={product.href} href={product.href} onClick={onClose} className={linkClassName}>
                  {product.name}
                </Link>
              ))}
            </div>
          )}
          <button
            onClick={() => setIsSolusiOpen(!isSolusiOpen)}
            className={`flex items-center justify-between w-full text-lg font-medium ${mobileMenuText} ${isTransparent ? 'hover:text-white/80' : 'hover:text-neutral-600'} transition-colors px-4 py-3 rounded-lg ${mobileMenuHover}`}
          >
            Solusi
            <ChevronDown
              size={18}
              className={`transition-transform ${isSolusiOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {isSolusiOpen && (
            <div className="flex flex-col gap-1">
              {SOLUTIONS.map((solution) => (
                <Link key={solution.slug} href={solution.href} onClick={onClose} className={linkClassName}>
                  {solution.name}
                </Link>
              ))}
            </div>
          )}
          <button
            onClick={() => setIsKajianOpen(!isKajianOpen)}
            className={`flex items-center justify-between w-full text-lg font-medium ${mobileMenuText} ${isTransparent ? 'hover:text-white/80' : 'hover:text-neutral-600'} transition-colors px-4 py-3 rounded-lg ${mobileMenuHover}`}
          >
            Kajian
            <ChevronDown
              size={18}
              className={`transition-transform ${isKajianOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {isKajianOpen && (
            <div className="flex flex-col gap-1">
              <Link
                href={KAJIAN_EVENT_LINK.href}
                onClick={onClose}
                className={linkClassName}
              >
                {KAJIAN_EVENT_LINK.name}
              </Link>
              <div className={`border-t ${isTransparent ? 'border-white/10' : 'border-neutral-200'} my-1`} />
              {categories.map((category) =>
                category.articleCount === 0 ? (
                  <div key={category.slug} aria-disabled="true" className={`text-base font-medium ${mobileMenuText} opacity-40 cursor-default px-4 py-3 rounded-lg`}>
                    {category.name}
                  </div>
                ) : (
                  <Link key={category.slug} href={`/blog/posts?category=${category.slug}`} onClick={onClose} className={linkClassName}>
                    {category.name}
                  </Link>
                )
              )}
            </div>
          )}
          {NAV_LINKS.filter((item) => item.key !== 'kajian').map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={onClose}
              className={`text-lg font-medium ${mobileMenuText} ${isTransparent ? 'hover:text-white/80' : 'hover:text-neutral-600'} transition-colors px-4 py-3 rounded-lg ${mobileMenuHover}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className={`pt-4 border-t ${isTransparent ? 'border-white/10' : 'border-neutral-200'}`}>
          <Link
            href="/id/home"
            onClick={onClose}
            className={`flex items-center justify-center w-full px-6 py-3.5 ${isTransparent ? 'bg-white text-neutral-900 hover:bg-neutral-100' : 'bg-neutral-900 text-white hover:bg-neutral-800'} rounded-xl font-bold text-base transition-colors`}
          >
            Mulai Riset Hukum
          </Link>
        </div>
      </div>
    </div>
  );
}
